// frontend/src/components/asset/MyAccidents.js
/**
 * Driver-facing open accidents on /my-assets.
 *
 * Self-hides when the queue is empty (same pattern as MyTrafficOffences).
 * Drivers can upload scene photos and the Singapore Accident Statement for
 * accidents still awaiting documents.
 *
 * Theme: Employee (blue), with amber/red reserved for insurer-clock urgency.
 */

import React, { useState } from 'react';
import {
  Siren, CalendarClock, Car, MapPin, Upload, Camera, FileText,
} from 'lucide-react';
import Modal from '../UI/Modal';
import { StatusBadge } from '../UI/StatusBadge';
import SecureFileLink from './SecureFileLink';
import {
  ReturnPhotoUploader,
  validateReturnPhotosForSubmit,
} from './PendingReturnsShared';
import { formatDateTime } from '../../utils/dateUtils';
import { assetInventoryAPI } from '../../services/api';
import toast from '../../utils/toast';
import {
  useMyAccidents,
  useUploadMyAccidentPhotos,
  useUploadMyAccidentSas,
} from '../../hooks/useAssetInventoryData';
import { accidentBadgeStatusFor } from './AccidentDetailModal';

const ACCIDENT_PHOTO_MAX = 15;
const ACCIDENT_PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const ACCIDENT_PHOTOS_TOTAL_BYTES = 75 * 1024 * 1024;

const hoursUntil = (iso) => {
  if (!iso) return null;
  return (new Date(iso) - new Date()) / 3600000;
};

const InsurerStrip = ({ accident }) => {
  if (accident.insurer_notified_at || accident.is_legacy_import) return null;
  if (!accident.insurer_report_deadline) return null;

  const hours = hoursUntil(accident.insurer_report_deadline);
  const tone = hours < 0
    ? 'bg-red-50 text-red-800 border-red-200'
    : hours <= 12
      ? 'bg-amber-50 text-amber-800 border-amber-200'
      : 'bg-gray-50 text-gray-700 border-gray-200';
  const text = hours < 0
    ? `Insurer notification overdue by ${Math.abs(Math.round(hours))}h`
    : hours < 1
      ? `Insurer notification due in ${Math.max(1, Math.round(hours * 60))}m`
      : `${Math.round(hours)}h left to notify insurer`;

  return (
    <div className={`rounded-lg border px-2.5 py-1.5 flex items-center gap-1.5 ${tone}`}>
      <CalendarClock className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="text-xs font-medium">{text}</span>
    </div>
  );
};

const Fact = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1 text-xs text-gray-600">
    <Icon className="w-3 h-3 text-gray-400 flex-shrink-0" />
    {children}
  </span>
);

const UploadPhotosModal = ({ isOpen, onClose, accident }) => {
  const [photos, setPhotos] = useState([]);
  const upload = useUploadMyAccidentPhotos();

  const close = () => {
    setPhotos([]);
    onClose();
  };

  const submit = (e) => {
    e.preventDefault();
    const err = validateReturnPhotosForSubmit(photos, {
      maxPhotos: ACCIDENT_PHOTO_MAX,
      maxBytes: ACCIDENT_PHOTO_MAX_BYTES,
      totalBytes: ACCIDENT_PHOTOS_TOTAL_BYTES,
    });
    if (err) {
      toast.error(err);
      return;
    }
    if (!photos.length) return;
    upload.mutate(
      { accidentId: accident.id, photos },
      { onSuccess: close },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title="Upload Accident Photos"
      size="md"
      theme="employee"
      footer={
        <>
          <button type="button" onClick={close} className="btn btn-outline">Cancel</button>
          <button
            type="submit"
            form="my-accident-photos-form"
            disabled={!photos.length || upload.isPending}
            className="btn btn-primary"
          >
            <Camera className="w-4 h-4 mr-2" />
            {upload.isPending ? 'Uploading...' : 'Upload Photos'}
          </button>
        </>
      }
    >
      <form id="my-accident-photos-form" onSubmit={submit} className="space-y-4">
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-sm font-medium text-blue-900">{accident?.reference}</p>
          <p className="text-xs text-blue-700">{accident?.vehicle_number_raw}</p>
        </div>
        <ReturnPhotoUploader
          photos={photos}
          onChange={setPhotos}
          label="Scene photos"
          maxPhotos={ACCIDENT_PHOTO_MAX}
          maxBytes={ACCIDENT_PHOTO_MAX_BYTES}
          totalBytes={ACCIDENT_PHOTOS_TOTAL_BYTES}
          theme="employee"
        />
      </form>
    </Modal>
  );
};

const UploadSasModal = ({ isOpen, onClose, accident }) => {
  const [file, setFile] = useState(null);
  const upload = useUploadMyAccidentSas();

  const close = () => {
    setFile(null);
    onClose();
  };

  const submit = (e) => {
    e.preventDefault();
    if (!file) return;
    upload.mutate(
      { accidentId: accident.id, file },
      { onSuccess: close },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title="Upload Singapore Accident Statement"
      size="sm"
      theme="employee"
      footer={
        <>
          <button type="button" onClick={close} className="btn btn-outline">Cancel</button>
          <button
            type="submit"
            form="my-accident-sas-form"
            disabled={!file || upload.isPending}
            className="btn btn-primary"
          >
            <Upload className="w-4 h-4 mr-2" />
            {upload.isPending ? 'Uploading...' : 'Upload SAS'}
          </button>
        </>
      }
    >
      <form id="my-accident-sas-form" onSubmit={submit} className="space-y-4">
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-sm font-medium text-blue-900">{accident?.reference}</p>
          <p className="text-xs text-blue-700">{accident?.vehicle_number_raw}</p>
        </div>
        <div>
          <label htmlFor="my-accident-sas-file" className="block text-sm font-medium text-gray-700 mb-1">
            SAS document <span className="text-red-500">*</span>
          </label>
          <input
            id="my-accident-sas-file"
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="input w-full"
          />
        </div>
      </form>
    </Modal>
  );
};

const AccidentCard = ({ row, onUploadPhotos, onUploadSas }) => (
  <div className="bg-white rounded-xl border border-amber-200 p-4 space-y-3">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{row.reference}</p>
        <p className="text-xs text-gray-400 truncate">{row.accident_type_display}</p>
      </div>
      <StatusBadge status={accidentBadgeStatusFor(row.status)} label={row.status_display} />
    </div>

    <div className="flex flex-wrap gap-x-3 gap-y-1">
      <Fact icon={Car}>{row.vehicle_number_raw}</Fact>
      <Fact icon={CalendarClock}>{formatDateTime(row.accident_datetime)}</Fact>
      {row.location && <Fact icon={MapPin}>{row.location}</Fact>}
    </div>

    <InsurerStrip accident={row} />

    <div className="flex flex-col sm:flex-row gap-2">
      <button type="button" onClick={() => onUploadPhotos(row)} className="btn btn-primary">
        <Camera className="w-4 h-4 mr-2" />
        Upload Photos
      </button>
      {row.has_sas ? (
        <SecureFileLink
          url={assetInventoryAPI.myAccidentSasUrl(row.id)}
          label="View SAS"
          theme="employee"
          icon={FileText}
        />
      ) : (
        <button type="button" onClick={() => onUploadSas(row)} className="btn btn-outline">
          <Upload className="w-4 h-4 mr-2" />
          Upload SAS
        </button>
      )}
    </div>

    <p className="text-xs text-gray-500">
      {row.photo_count > 0
        ? `${row.photo_count} photo(s) on file${row.has_sas ? ' · SAS uploaded' : ' · SAS still needed'}.`
        : 'Please upload scene photos and your Singapore Accident Statement.'}
    </p>
  </div>
);

const MyAccidents = () => {
  const { data: rows = [] } = useMyAccidents();
  const [photoRow, setPhotoRow] = useState(null);
  const [sasRow, setSasRow] = useState(null);

  if (rows.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-amber-200 p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
          <Siren className="w-4 h-4 text-amber-700" />
        </div>
        <div className="min-w-0">
          <h2 className="text-sm font-bold text-gray-900">
            Vehicle Accidents ({rows.length})
          </h2>
          <p className="text-xs text-gray-500">
            Photos or SAS still needed — please complete these first.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {rows.map((row) => (
          <AccidentCard
            key={row.id}
            row={row}
            onUploadPhotos={setPhotoRow}
            onUploadSas={setSasRow}
          />
        ))}
      </div>

      <UploadPhotosModal
        isOpen={!!photoRow}
        onClose={() => setPhotoRow(null)}
        accident={photoRow}
      />
      <UploadSasModal
        isOpen={!!sasRow}
        onClose={() => setSasRow(null)}
        accident={sasRow}
      />
    </div>
  );
};

export default MyAccidents;
